if (!customElements.get('nfd-editorial-menu')) {
  const collectionPreviewCache = new Map();
  const pagePreviewCache = new Map();

  customElements.define(
    'nfd-editorial-menu',
    class NfdEditorialMenu extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.items = Array.from(this.querySelectorAll('[data-nfd-menu-item]'));
        this.details = Array.from(this.querySelectorAll('[data-nfd-menu-detail]'));
        this.secondaryLinks = Array.from(this.querySelectorAll('[data-nfd-menu-secondary]'));
        this.previews = Array.from(this.querySelectorAll('[data-nfd-menu-preview]'));
        this.activeIndex = 0;

        this.addEventListener('pointerover', this.onPointerOver.bind(this), {
          signal: this.abortController.signal,
        });
        this.addEventListener('focusin', this.onFocusIn.bind(this), {
          signal: this.abortController.signal,
        });
        const menuDialog = this.closest('header-mega-menu');

        menuDialog?.addEventListener(
          'dialog:before-show',
          () => {
            this.resetSecondaryFocus();
            this.activate(0);
          },
          { signal: this.abortController.signal },
        );
        menuDialog?.addEventListener(
          'dialog:after-hide',
          () => {
            this.resetSecondaryFocus();
            this.activate(0);
          },
          { signal: this.abortController.signal },
        );
      }

      disconnectedCallback() {
        window.clearTimeout(this.collectionPreviewTimer);
        window.clearTimeout(this.pagePreviewTimer);
        window.clearTimeout(this.previewTimer);
        this.abortController?.abort();
      }

      onPointerOver(event) {
        const secondaryLink = event.target.closest('[data-nfd-menu-secondary]');

        if (
          secondaryLink &&
          this.contains(secondaryLink) &&
          (!event.relatedTarget || !secondaryLink.contains(event.relatedTarget))
        ) {
          this.focusSecondary(secondaryLink);
          return;
        }

        const item = event.target.closest('[data-nfd-menu-item]');

        if (item && this.contains(item)) {
          this.resetSecondaryFocus();
          this.activate(Number(item.dataset.index));
        }
      }

      onFocusIn(event) {
        const secondaryLink = event.target.closest('[data-nfd-menu-secondary]');

        if (secondaryLink && this.contains(secondaryLink)) {
          this.focusSecondary(secondaryLink, { immediate: true });
          return;
        }

        const item = event.target.closest('[data-nfd-menu-item]');

        if (item) {
          this.resetSecondaryFocus();
          this.activate(Number(item.dataset.index));
        }
      }

      focusSecondary(link, { immediate = false } = {}) {
        const previewKey = link.dataset.previewKey;
        const nextPreview = previewKey
          ? this.previews.find((preview) => preview.dataset.previewKey === previewKey)
          : null;

        this.classList.add('is-secondary-focused');

        this.secondaryLinks.forEach((secondaryLink) => {
          secondaryLink.closest('li')?.classList.toggle('is-selected', secondaryLink === link);
        });

        window.clearTimeout(this.collectionPreviewTimer);
        window.clearTimeout(this.pagePreviewTimer);

        if (nextPreview?.dataset.previewKind === 'collection') {
          if (immediate) {
            this.loadCollectionPreview(nextPreview);
          } else {
            this.collectionPreviewTimer = window.setTimeout(() => this.loadCollectionPreview(nextPreview), 160);
          }
        } else if (nextPreview?.dataset.previewKind === 'page') {
          if (immediate) {
            this.loadPagePreview(nextPreview);
          } else {
            this.pagePreviewTimer = window.setTimeout(() => this.loadPagePreview(nextPreview), 180);
          }
        } else {
          nextPreview?.querySelector('img')?.setAttribute('loading', 'eager');
        }

        this.swapPreview(nextPreview);
      }

      loadCollectionPreview(preview) {
        const collectionHandle = preview.dataset.collectionHandle;
        const stage = preview.querySelector('[data-nfd-collection-stage]');

        if (!collectionHandle || !stage || preview.dataset.loadState === 'loaded') return;
        if (preview.dataset.loadState === 'loading') return;

        preview.dataset.loadState = 'loading';

        let previewRequest = collectionPreviewCache.get(collectionHandle);

        if (!previewRequest) {
          previewRequest = this.fetchCollectionPreview(collectionHandle);
          collectionPreviewCache.set(collectionHandle, previewRequest);
          previewRequest.catch(() => collectionPreviewCache.delete(collectionHandle));
        }

        previewRequest
          .then((markup) => {
            if (!this.isConnected) return;

            const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
            const renderedPreview = parsedDocument.querySelector('[data-nfd-collection-product-preview]');

            if (!renderedPreview) throw new Error('Collection preview section did not render.');

            stage.replaceChildren(renderedPreview);
            stage.classList.remove('is-error');
            preview.dataset.loadState = 'loaded';
          })
          .catch(() => {
            if (!this.isConnected) return;

            const loader = stage.querySelector('[data-nfd-collection-loader]');

            if (loader) loader.textContent = 'Collection preview unavailable';

            stage.classList.add('is-error');
            preview.dataset.loadState = 'error';
          });
      }

      async fetchCollectionPreview(collectionHandle) {
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(`${root}collections/${encodeURIComponent(collectionHandle)}`, window.location.origin);

        url.searchParams.set('section_id', 'nfd-menu-collection-products');

        const response = await fetch(url.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          priority: 'low',
        });

        if (!response.ok) throw new Error(`Collection preview request failed with ${response.status}.`);

        return response.text();
      }

      loadPagePreview(preview) {
        const previewUrl = preview.dataset.previewUrl;
        const stage = preview.querySelector('[data-nfd-page-stage]');

        if (!previewUrl || !stage || preview.dataset.loadState === 'loaded') return;
        if (preview.dataset.loadState === 'loading' || preview.dataset.loadState === 'external') return;

        const url = new URL(previewUrl, window.location.origin);

        if (url.origin !== window.location.origin) {
          stage.querySelector('[data-nfd-page-loader]')?.remove();
          preview.dataset.loadState = 'external';
          return;
        }

        preview.dataset.loadState = 'loading';
        stage.classList.remove('is-error');
        stage.classList.add('is-loading');

        url.hash = '';
        const cacheKey = url.toString();
        let previewRequest = pagePreviewCache.get(cacheKey);

        if (!previewRequest) {
          previewRequest = this.fetchPagePreview(url);
          pagePreviewCache.set(cacheKey, previewRequest);
          previewRequest.catch(() => pagePreviewCache.delete(cacheKey));
        }

        previewRequest
          .then((images) => {
            if (!this.isConnected) return;

            if (images.length === 0) throw new Error('Page preview images were not found.');

            stage.replaceChildren(this.buildPageContactSheet(images));
            stage.classList.remove('is-loading');
            stage.classList.add('is-ready');
            preview.dataset.loadState = 'loaded';
          })
          .catch(() => {
            if (!this.isConnected) return;

            const loader = stage.querySelector('[data-nfd-page-loader]');

            if (loader) loader.textContent = 'Open page';

            stage.classList.remove('is-loading');
            stage.classList.add('is-error');
            preview.dataset.loadState = 'error';
          });
      }

      async fetchPagePreview(url) {
        const response = await fetch(url.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          priority: 'low',
        });

        if (!response.ok) throw new Error(`Page preview request failed with ${response.status}.`);

        const contentType = response.headers.get('content-type') || '';

        if (!contentType.includes('text/html')) throw new Error('Page preview did not return HTML.');

        return this.extractPagePreviewImages(await response.text(), url);
      }

      extractPagePreviewImages(markup, url) {
        const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
        const main = parsedDocument.querySelector('main#main, main, [role="main"]');

        if (!main) throw new Error('Page preview content was not found.');

        const images = Array.from(main.querySelectorAll('img'));
        const selectedImages = [];
        const selectedKeys = new Set();
        const excludedImagePattern = /avatar|badge|flag|icon|logo|payment|placeholder|spinner|swatch/i;

        const collectImages = (minimumWidth, minimumHeight, allowUnknownDimensions = false) => {
          images.forEach((image) => {
            if (selectedImages.length >= 3) return;
            if (image.closest('[hidden], [aria-hidden="true"], header, footer, nav')) return;

            const imageIdentity = [image.className, image.getAttribute('alt'), image.getAttribute('title')]
              .filter(Boolean)
              .join(' ');

            if (excludedImagePattern.test(imageIdentity)) return;

            const source = this.getPagePreviewImageSource(image, url);

            if (!source || /\.(gif|svg)(?:$|\?)/i.test(source)) return;

            const width = this.getImageDimension(image, 'width') || this.getLargestSrcsetWidth(image);
            const height = this.getImageDimension(image, 'height');
            const hasKnownDimensions = width > 0 || height > 0;

            if (allowUnknownDimensions && hasKnownDimensions) return;
            if (!allowUnknownDimensions && !hasKnownDimensions) return;
            if (width > 0 && width < minimumWidth) return;
            if (height > 0 && height < minimumHeight) return;

            const sourceUrl = new URL(source, url);
            const imageKey = `${sourceUrl.origin}${sourceUrl.pathname}`;

            if (selectedKeys.has(imageKey)) return;

            selectedKeys.add(imageKey);
            selectedImages.push(sourceUrl.toString());
          });
        };

        collectImages(480, 280);
        if (selectedImages.length < 3) collectImages(280, 180);
        if (selectedImages.length < 3) collectImages(0, 0, true);

        return selectedImages.slice(0, 3);
      }

      getPagePreviewImageSource(image, url) {
        try {
          const srcset = image.getAttribute('srcset') || image.getAttribute('data-srcset');

          if (srcset) {
            const candidates = srcset
              .split(',')
              .map((candidate) => {
                const parts = candidate.trim().split(/\s+/);
                const descriptor = parts.at(-1);
                const hasDescriptor = /^(?:\d+(?:\.\d+)?[wx])$/.test(descriptor);
                const source = hasDescriptor ? parts.slice(0, -1).join(' ') : candidate.trim();
                const score = descriptor.endsWith('w')
                  ? Number.parseFloat(descriptor)
                  : descriptor.endsWith('x')
                    ? Number.parseFloat(descriptor) * 800
                    : 0;

                return { source, score };
              })
              .filter((candidate) => candidate.source && !candidate.source.startsWith('data:'))
              .sort((first, second) => first.score - second.score);
            const preferredCandidate = candidates.find((candidate) => candidate.score >= 900) || candidates.at(-1);

            if (preferredCandidate) return new URL(preferredCandidate.source, url).toString();
          }

          const source = image.getAttribute('data-src') || image.getAttribute('src');

          if (!source || source.startsWith('data:')) return '';

          return new URL(source, url).toString();
        } catch {
          return '';
        }
      }

      getImageDimension(image, attribute) {
        const value = image.getAttribute(attribute) || '';

        return /^\d+$/.test(value) ? Number.parseInt(value, 10) : 0;
      }

      getLargestSrcsetWidth(image) {
        const srcset = image.getAttribute('srcset') || image.getAttribute('data-srcset') || '';

        return Math.max(
          0,
          ...Array.from(srcset.matchAll(/\s(\d+)w(?:,|$)/g), (match) => Number.parseInt(match[1], 10)),
        );
      }

      buildPageContactSheet(images) {
        const contactSheet = document.createElement('div');

        contactSheet.className = 'nfd-editorial-menu__contact-sheet';
        contactSheet.dataset.imageCount = String(images.length);

        images.forEach((source, index) => {
          const tile = document.createElement('div');
          const image = document.createElement('img');

          tile.className = 'nfd-editorial-menu__contact-tile';
          image.className = 'nfd-editorial-menu__contact-image';
          image.alt = '';
          image.decoding = 'async';
          image.loading = 'eager';
          image.src = source;
          image.style.setProperty('--nfd-contact-index', index);
          tile.append(image);
          contactSheet.append(tile);
        });

        return contactSheet;
      }

      resetSecondaryFocus() {
        window.clearTimeout(this.collectionPreviewTimer);
        window.clearTimeout(this.pagePreviewTimer);
        this.classList.remove('is-secondary-focused');

        this.secondaryLinks.forEach((secondaryLink) => {
          secondaryLink.closest('li')?.classList.remove('is-selected');
        });

        this.swapPreview(null);
      }

      swapPreview(nextPreview) {
        const previousPreview = this.previews.find((preview) => preview.classList.contains('is-active'));

        if (previousPreview === nextPreview) return;

        const previousIndex = this.previews.indexOf(previousPreview);
        const nextIndex = this.previews.indexOf(nextPreview);
        const direction = previousIndex < 0 || nextIndex >= previousIndex ? 1 : -1;

        this.style.setProperty('--nfd-menu-direction', direction);
        window.clearTimeout(this.previewTimer);

        previousPreview?.classList.remove('is-active');
        previousPreview?.classList.add('is-leaving');
        previousPreview?.setAttribute('aria-hidden', 'true');
        previousPreview?.querySelector('[data-nfd-preview-link]')?.setAttribute('tabindex', '-1');
        nextPreview?.classList.remove('is-leaving');
        nextPreview?.classList.add('is-active');
        nextPreview?.setAttribute('aria-hidden', 'false');
        nextPreview?.querySelector('[data-nfd-preview-link]')?.setAttribute('tabindex', '0');

        this.previewTimer = window.setTimeout(() => previousPreview?.classList.remove('is-leaving'), 650);
      }

      activate(index) {
        if (!Number.isInteger(index) || index < 0 || index >= this.items.length) return;

        this.items.forEach((item) => {
          const isActive = Number(item.dataset.index) === index;

          item.classList.toggle('is-active', isActive);
          item.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', String(isActive));
        });

        this.details.forEach((detail) => {
          const isActive = Number(detail.dataset.index) === index;
          detail.hidden = !isActive;
          detail.classList.toggle('is-active', isActive);
        });

        this.activeIndex = index;
      }
    },
  );
}

if (!customElements.get('nfd-editorial-drawer-reset')) {
  customElements.define(
    'nfd-editorial-drawer-reset',
    class NfdEditorialDrawerReset extends HTMLElement {
      connectedCallback() {
        this.drawer = this.closest('header-menu-drawer.menu-drawer--nfd-editorial');
        this.onAfterHide = this.reset.bind(this);
        this.onClick = this.handleClick.bind(this);
        this.onPageShow = this.clearNavigationState.bind(this);
        this.drawer?.addEventListener('dialog:after-hide', this.onAfterHide);
        this.drawer?.addEventListener('click', this.onClick);
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        this.drawer?.removeEventListener('dialog:after-hide', this.onAfterHide);
        this.drawer?.removeEventListener('click', this.onClick);
        window.removeEventListener('pageshow', this.onPageShow);
      }

      handleClick(event) {
        const link = event.target.closest('a[data-nfd-collection-link]');

        if (
          !link ||
          !this.drawer?.contains(link) ||
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          link.target === '_blank' ||
          link.hasAttribute('download')
        ) {
          return;
        }

        this.clearNavigationState();
        link.classList.add('is-navigating');
        link.setAttribute('aria-busy', 'true');
      }

      clearNavigationState() {
        this.drawer?.querySelectorAll('[data-nfd-collection-link].is-navigating').forEach((link) => {
          link.classList.remove('is-navigating');
          link.removeAttribute('aria-busy');
        });
      }

      reset() {
        this.drawer?.querySelectorAll('details.nfd-editorial-drawer[open]').forEach((details) => {
          details.open = false;
        });

        this.clearNavigationState();
      }
    },
  );
}
